import { CartAddEvent, ThemeEvents } from '@theme/events';

/**
 * JJ Shine free-shipping bar (snippets/jjs-free-ship.liquid).
 * Quick adds: add the item without leaving the page, then tell the theme the cart changed so the
 * cart drawer / cart page re-render (the bar is part of that render). If anything fails, the plain
 * form post goes through instead and lands on the cart page.
 * Product page note: keeps "You're $X away from FREE shipping (lower 48)" in step with the cart.
 */

const root = () => window.Shopify?.routes?.root ?? '/';

/**
 * @param {number} cents
 * @returns {string} "$26" or "$26.50"
 */
const dollars = (cents) => '$' + (cents % 100 === 0 ? String(cents / 100) : (cents / 100).toFixed(2));

document.addEventListener('submit', async (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.matches('[data-jjs-fs-add]')) return;

  const button = form.querySelector('button');
  if (!button) return;

  event.preventDefault();
  if (button.disabled) return;

  const label = button.textContent;
  button.disabled = true;
  button.textContent = 'Adding';

  try {
    const id = Number(new FormData(form).get('id'));
    const response = await fetch(`${root()}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id, quantity: 1 }] }),
    });
    if (!response.ok) throw new Error(`cart/add ${response.status}`);

    const cart = await (await fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } })).json();

    document.dispatchEvent(
      new CartAddEvent(cart, 'jjs-free-ship', {
        source: 'jjs-free-ship',
        itemCount: cart.item_count,
        variantId: String(id),
      })
    );
  } catch (error) {
    console.error(error);
    button.disabled = false;
    button.textContent = label;
    form.submit();
  }
});

/**
 * @param {{ total_price?: number, item_count?: number } | undefined} cart
 */
const updateNotes = async (cart) => {
  const notes = document.querySelectorAll('[data-jjs-fs-note]');
  if (notes.length === 0) return;

  if (typeof cart?.total_price !== 'number' || typeof cart?.item_count !== 'number') {
    cart = await (await fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } })).json();
  }

  for (const note of notes) {
    if (!(note instanceof HTMLElement)) continue;

    const freeAt = Number(note.dataset.freeAt);
    const total = Number(cart?.total_price ?? 0);
    const gap = freeAt - total;
    const message = note.querySelector('[data-jjs-fs-note-msg]');
    const fill = note.querySelector('[data-jjs-fs-note-fill]');

    note.hidden = !cart?.item_count;
    note.dataset.state = gap > 0 ? 'away' : 'unlocked';
    if (message) {
      message.textContent = gap > 0 ? `You're ${dollars(gap)} away from FREE shipping (lower\u00a048\u00a0states)` : "You've unlocked FREE shipping (lower\u00a048\u00a0states)";
    }
    if (fill instanceof HTMLElement) {
      fill.style.width = `${Math.max(0, Math.min(100, Math.floor((total * 100) / freeAt)))}%`;
    }
  }
};

/**
 * Free shipping reached: the bar (and the product page note) plays a short animation once, the moment the cart crosses $99
 * (Jordan, Oct 15 2026: "once free shipping gets hit I feel like there should be some kind of slight animation"). The cart's last
 * state is kept for this visit, so a cart that is already over $99 does not play it again on every page. When the drawer is closed
 * the animation waits and plays when it opens; it is never played for shoppers who ask for less motion.
 */
const FREE_AT = 9900;
const kept = (key, value) => {
  try {
    if (value === undefined) return sessionStorage.getItem(key);
    if (value === null) sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, value);
  } catch {}
  return null;
};
let party = kept('jjs-fs-party') === '1';

const celebrate = () => {
  if (!party) return;
  for (const el of document.querySelectorAll('.jjs-fs[data-state="unlocked"], [data-jjs-fs-note][data-state="unlocked"]')) {
    if (!(el instanceof HTMLElement) || el.classList.contains('is-celebrate')) continue;
    el.style.setProperty('--jjs-fs-from', `${kept('jjs-fs-from') || 85}%`);
    el.classList.add('is-celebrate');
  }
};

/** @param {{ total_price: number, item_count: number }} cart */
const track = (cart) => {
  const now = cart.item_count > 0 && cart.total_price >= FREE_AT ? 'unlocked' : 'away';
  const calm = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  if (kept('jjs-fs-last') === 'away' && now === 'unlocked' && !calm) {
    party = true;
    kept('jjs-fs-party', '1');
  }
  if (now === 'away') {
    party = false;
    kept('jjs-fs-party', null);
    kept('jjs-fs-from', String(Math.max(0, Math.min(100, Math.floor((cart.total_price * 100) / FREE_AT)))));
  }
  kept('jjs-fs-last', now);
};

// first page of the visit: take the cart's state from the bar the page was drawn with
if (kept('jjs-fs-last') == null) {
  const bar = document.querySelector('.jjs-fs');
  kept('jjs-fs-last', bar instanceof HTMLElement && bar.dataset.state === 'unlocked' ? 'unlocked' : 'away');
  const fill = bar?.querySelector('.jjs-fs__fill');
  if (fill instanceof HTMLElement && fill.style.width) kept('jjs-fs-from', String(parseInt(fill.style.width, 10)));
}

// the bar is drawn again whenever the cart changes: mark the new one too while the animation is still owed
new MutationObserver(() => party && requestAnimationFrame(celebrate)).observe(document.body, {
  childList: true,
  subtree: true,
  attributes: true,
  attributeFilter: ['data-state'],
});

// played (the drawer was open): done for this crossing
document.addEventListener('animationend', (event) => {
  const el = event.target;
  if (!(el instanceof HTMLElement)) return;
  if (event.animationName === 'jjsFsGlow' && el.matches('.jjs-fs')) {
    party = false;
    kept('jjs-fs-party', null);
    el.classList.remove('is-celebrate');
  }
  if (event.animationName === 'jjsFsRise' && el.matches('.jjs-fs-note__msg')) el.closest('.jjs-fs-note')?.classList.remove('is-celebrate');
});

document.addEventListener(ThemeEvents.cartUpdate, async (event) => {
  try {
    let cart = /** @type {CustomEvent} */ (event).detail?.resource;
    if (typeof cart?.total_price !== 'number' || typeof cart?.item_count !== 'number') {
      cart = await (await fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } })).json();
    }
    track(cart);
    await updateNotes(cart);
    celebrate();
  } catch (error) {
    console.error(error);
  }
});

// A page restored from the back/forward cache shows the cart as it was when the page was left.
window.addEventListener('pageshow', (event) => {
  if (event.persisted) updateNotes(undefined).catch(console.error);
});
